import React, { useState, useEffect } from 'react';
import { Navigation } from '@/components/layout/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { getCommunityReports, createCommunityReport, upvoteReport } from '@/lib/api';
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  ThumbsUp, 
  Calendar,
  CheckCircle,
  Loader2,
  Eye,
  Link as LinkIcon
} from 'lucide-react';
import { format } from 'date-fns';

const CommunityPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [newReport, setNewReport] = useState({
    product_name: '',
    barcode: '',
    hidden_ingredient: '',
    description: '',
    source: ''
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const data = await getCommunityReports();
      setReports(data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newReport.product_name || !newReport.hidden_ingredient || !newReport.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const report = await createCommunityReport(newReport);
      setReports([report, ...reports]);
      setNewReport({
        product_name: '',
        barcode: '',
        hidden_ingredient: '',
        description: '',
        source: ''
      });
      setDialogOpen(false);
      toast.success('Report submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpvote = async (reportId) => {
    try {
      await upvoteReport(reportId);
      setReports(reports.map(r => 
        r.id === reportId ? { ...r, upvotes: r.upvotes + 1 } : r
      ));
      toast.success('Thanks for your vote!');
    } catch (error) {
      toast.error('Failed to upvote');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="community-page">
      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
              Community Reports
            </h1>
            <p className="text-muted-foreground text-lg">
              Share and discover hidden ingredients reported by the community
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-report-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Report
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Report Hidden Ingredient</DialogTitle>
                <DialogDescription>
                  Help the community by reporting hidden ingredients you've discovered
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="product_name">Product Name *</Label>
                  <Input
                    id="product_name"
                    placeholder="e.g., Brand XYZ Energy Drink"
                    value={newReport.product_name}
                    onChange={(e) => setNewReport({ ...newReport, product_name: e.target.value })}
                    required
                    data-testid="report-product-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="barcode">Barcode (optional)</Label>
                  <Input
                    id="barcode"
                    placeholder="e.g., 012345678901"
                    value={newReport.barcode}
                    onChange={(e) => setNewReport({ ...newReport, barcode: e.target.value })}
                    data-testid="report-barcode"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="hidden_ingredient">Hidden Ingredient *</Label>
                  <Input
                    id="hidden_ingredient"
                    placeholder="e.g., MSG listed as 'Natural Flavors'"
                    value={newReport.hidden_ingredient}
                    onChange={(e) => setNewReport({ ...newReport, hidden_ingredient: e.target.value })}
                    required
                    data-testid="report-ingredient"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Explain how you discovered this hidden ingredient..."
                    value={newReport.description}
                    onChange={(e) => setNewReport({ ...newReport, description: e.target.value })}
                    className="min-h-[100px]"
                    required
                    data-testid="report-description"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="source">Source Link (optional)</Label>
                  <Input
                    id="source"
                    placeholder="https://..."
                    value={newReport.source}
                    onChange={(e) => setNewReport({ ...newReport, source: e.target.value })}
                    data-testid="report-source"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="submit-report-btn">
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Report'
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Reports List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : reports.length > 0 ? (
          <div className="space-y-4" data-testid="reports-list">
            {reports.map((report) => (
              <Card key={report.id} className="border border-border">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-1">
                      {/* Product Name */}
                      <h3 className="font-semibold text-lg mb-1" data-testid={`report-title-${report.id}`}>
                        {report.product_name}
                      </h3>
                      
                      {/* Hidden Ingredient */}
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                          <Eye className="w-3 h-3 mr-1" />
                          {report.hidden_ingredient}
                        </Badge>
                        {report.verified && (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                      </div>
                      
                      {/* Description */}
                      <p className="text-muted-foreground text-sm mb-3">
                        {report.description}
                      </p>
                      
                      {/* Meta */}
                      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                        <span>Reported by {report.user_name}</span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(report.created_at), 'MMM d, yyyy')}
                        </span>
                        {report.source && (
                          <a 
                            href={report.source} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <LinkIcon className="w-3 h-3" />
                            Source
                          </a>
                        )}
                      </div>
                    </div>
                    
                    {/* Upvote */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                      onClick={() => handleUpvote(report.id)}
                      data-testid={`upvote-btn-${report.id}`}
                    >
                      <ThumbsUp className="w-4 h-4" />
                      {report.upvotes}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border border-dashed border-border">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No reports yet</h3>
              <p className="text-muted-foreground mb-6">
                Be the first to report a hidden ingredient!
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Submit First Report
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Info section */}
        <div className="mt-12 p-6 bg-muted/50 rounded-xl">
          <h3 className="font-semibold mb-3">How Community Reports Work</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                <span className="text-sm font-bold text-primary">1</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Submit a report when you discover a hidden ingredient in a product
              </p>
            </div>
            <div>
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                <span className="text-sm font-bold text-primary">2</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Community members upvote helpful reports to improve visibility
              </p>
            </div>
            <div>
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                <span className="text-sm font-bold text-primary">3</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Verified reports are incorporated into our AI analysis
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CommunityPage;
